import { useEffect, useRef, useState, ReactNode } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import '../pages/blog/blog-article.scss';

gsap.registerPlugin(ScrollTrigger);

const HERO_HEIGHT = 60;

// Types
interface ArticleMeta {
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  slug: string;
  publishedDate: string;
  modifiedDate?: string;
  category: string;
  readTime: string;
  heroImage: string;
  heroImageAlt: string;
  excerpt: string;
}

interface TocItem {
  id: string;
  label: string;
}

interface ParallaxBox {
  title: string;
  content: ReactNode;
}

interface ThreeBox {
  number: string;
  title: string;
  content: ReactNode;
}

interface TwoColumnSection {
  left: ReactNode;
  right: ReactNode;
}

interface RelatedPost {
  slug: string;
  title: string;
  category: string;
  readTime: string;
}

interface CtaBox {
  title: string;
  description: string;
  buttonText: string;
}

interface BlogArticleTemplateProps {
  meta: ArticleMeta;
  toc: TocItem[];
  sectionOneContent: ReactNode;
  parallaxBoxes: ParallaxBox[];
  threeBoxes: ThreeBox[];
  twoColumnSections: TwoColumnSection[];
  infoBoxContent?: ReactNode;
  conclusionContent: ReactNode;
  ctaBox: CtaBox;
  relatedPosts: RelatedPost[];
}

function BlogArticleTemplate({
  meta,
  toc,
  sectionOneContent,
  parallaxBoxes,
  threeBoxes,
  twoColumnSections,
  infoBoxContent,
  conclusionContent,
  ctaBox,
  relatedPosts
}: BlogArticleTemplateProps) {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [sidebarFixed, setSidebarFixed] = useState(false);
  const sectionOneRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (heroTitleRef.current) {
      gsap.fromTo(heroTitleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", delay: 0.2 }
      );
    }

    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setReadingProgress(progress);

      const heroThreshold = (window.innerHeight * HERO_HEIGHT) / 100;
      const sectionOneEnd = sectionOneRef.current
        ? sectionOneRef.current.offsetTop + sectionOneRef.current.offsetHeight
        : Infinity;

      setSidebarFixed(scrollTop > heroThreshold && scrollTop < sectionOneEnd - window.innerHeight * 0.3);
    };

    window.addEventListener('scroll', updateProgress);
    updateProgress();

    return () => {
      window.removeEventListener('scroll', updateProgress);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const baseUrl = 'https://www.camillacinodesigndev.it';

  return (
    <>
      <Helmet defer={false}>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:image" content={`${baseUrl}/loghi/sito/preview.png`} />
        <meta property="og:url" content={`${baseUrl}/blog/${meta.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={meta.publishedDate} />
        <meta property="article:author" content="Camilla Cino" />
        <link rel="canonical" href={`${baseUrl}/blog/${meta.slug}`} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": meta.ogTitle,
            "description": meta.ogDescription,
            "author": {
              "@type": "Person",
              "name": "Camilla Cino"
            },
            "datePublished": meta.publishedDate,
            "dateModified": meta.modifiedDate || meta.publishedDate,
            "image": `${baseUrl}/loghi/sito/preview.png`,
            "publisher": {
              "@type": "Organization",
              "name": "Camilla Cino Design & Dev",
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/loghi/sito/preview.png`
              }
            }
          })}
        </script>
      </Helmet>

      <div className="blog-article-page">
        {/* Reading Progress Bar */}
        <div className="reading-progress" style={{ width: `${readingProgress}%` }} />

        {/* Hero Section */}
        <section className="article-hero">
          <div className="hero-image">
            <img src={meta.heroImage} alt={meta.heroImageAlt} />
          </div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl">
            <div className="hero-content">
              <a href="/blog" onClick={(e) => { e.preventDefault(); navigateWithTransition('/blog'); }} className="back-link">
                <span className="arrow">←</span>
                Torna al blog
              </a>

              <div className="article-meta">
                <span className="article-category">{meta.category}</span>
                <span className="article-date">{meta.publishedDate} • {meta.readTime} lettura</span>
              </div>

              <h1 className="article-title" ref={heroTitleRef}>
                {meta.ogTitle}
              </h1>

              <p className="article-excerpt">
                {meta.excerpt}
              </p>
            </div>
          </Container>
        </section>

        {/* SEZIONE 1: Intro con Sidebar */}
        <section className="article-main" ref={sectionOneRef}>
          <Container maxWidth="xl">
            <div className="article-layout">
              <div className="article-content">
                {sectionOneContent}
              </div>

              {/* Sidebar */}
              <aside className="article-sidebar">
                <div className={`sidebar-sticky ${sidebarFixed ? 'is-fixed' : ''}`}>
                  {/* Author Card */}
                  <div className="author-card">
                    <div className="author-avatar">
                      <img src="/chi_sono/me.png" alt="Camilla Cino" />
                    </div>
                    <h3 className="author-name">Camilla Cino</h3>
                    <p className="author-role">Web Designer & Developer</p>
                    <p className="author-bio">
                      Creo siti web professionali e landing page ottimizzate per la conversione.
                    </p>
                  </div>

                  {/* Table of Contents */}
                  <div className="toc-card">
                    <h4 className="toc-title">In questo articolo</h4>
                    <ul className="toc-list">
                      {toc.map((item) => (
                        <li key={item.id}><a href={`#${item.id}`}>{item.label}</a></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </Container>
        </section>

        {/* SEZIONE 2: Parallax Boxes */}
        <section
          className="article-parallax-section"
          style={{
            background: `linear-gradient(180deg, #101010 0%, rgba(16, 16, 16, 0.7) 5%, rgba(16, 16, 16, 0.7) 95%, #101010 100%), url('${meta.heroImage}') center/cover fixed`
          }}
        >
          <Container maxWidth="xl">
            <div className="parallax-content">
              {parallaxBoxes.map((box, index) => (
                <div className="content-box" key={index}>
                  <h3>{box.title}</h3>
                  {box.content}
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* SEZIONE 3: Three Boxes */}
        <section className="article-three-boxes">
          <Container maxWidth="xl">
            <div className="three-boxes-grid">
              {threeBoxes.map((box, index) => (
                <div className="feature-box" key={index}>
                  <span className="box-number">{box.number}</span>
                  <h3>{box.title}</h3>
                  {box.content}
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* SEZIONE 4: Extra Content */}
        <section className="article-extra-content">
          <Container maxWidth="xl">
            {twoColumnSections.map((section, index) => (
              <div className="two-column-layout" key={index}>
                <div className="column-left">
                  {section.left}
                </div>
                <div className="column-right">
                  {section.right}
                </div>
              </div>
            ))}

            {infoBoxContent && (
              <div className="extra-content-wrapper">
                {infoBoxContent}
              </div>
            )}

            <div className="extra-content-wrapper">
              <h2>Conclusione</h2>
              {conclusionContent}

              <hr className="article-divider" />

              {/* CTA Box */}
              <div className="article-cta">
                <h3 className="cta-title">{ctaBox.title}</h3>
                <p className="cta-description">{ctaBox.description}</p>
                <button className="cta-button" onClick={() => navigateWithTransition('/contatti')}>
                  <span className="btn-text">{ctaBox.buttonText}</span>
                  <span className="arrow">→</span>
                </button>
              </div>
            </div>
          </Container>
        </section>

        {/* Related Posts */}
        <section className="related-section">
          <Container maxWidth="xl">
            <h3 className="section-title">Articoli Correlati</h3>
            <div className="related-grid">
              {relatedPosts.map((post) => (
                <div
                  key={post.slug}
                  className="related-card"
                  onClick={() => navigateWithTransition(`/blog/${post.slug}`)}
                >
                  <span className="related-category">{post.category}</span>
                  <h4 className="related-title">{post.title}</h4>
                  <span className="related-meta">{post.readTime} lettura</span>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Footer CTA Section */}
        <section className="footer-cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default BlogArticleTemplate;
